import type { StoreRegionCountry } from "@medusajs/types";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { medusa } from "#/lib/medusa";

const regionListSchema = z.object({
  regions: z.array(z.object({ id: z.string(), name: z.string() })),
});

const cityListSchema = z.object({
  cities: z.array(z.object({ id: z.string(), name: z.string() })),
});

const barangayListSchema = z.object({
  barangays: z.array(z.object({ id: z.string(), name: z.string() })),
});

export type CountryOption = {
  id: string;
  name: string;
  countryCode: string;
};

function toCountryOption(country: StoreRegionCountry): CountryOption | null {
  const code = country.iso_2?.toLowerCase();
  const name = country.display_name ?? country.name;
  if (!code || !name) return null;
  return { id: code, name, countryCode: code };
}

export const listCountries = createServerFn().handler(
  async (): Promise<CountryOption[]> => {
    const { regions } = await medusa.store.region.list({
      fields: "id,countries.iso_2,countries.name,countries.display_name",
      limit: 1000,
    });
    const seen = new Set<string>();
    const options: CountryOption[] = [];
    for (const region of regions) {
      for (const country of region.countries ?? []) {
        const option = toCountryOption(country);
        if (!option || seen.has(option.countryCode)) continue;
        seen.add(option.countryCode);
        options.push(option);
      }
    }
    options.sort((a, b) => a.name.localeCompare(b.name));
    return options;
  },
);

export const listRegions = createServerFn()
  .inputValidator(z.object({ countryCode: z.string().min(2).toLowerCase() }))
  .handler(async ({ data }) => {
    const res = await medusa.client.fetch<unknown>("/store/ph-geo/regions", {
      query: { country_code: data.countryCode },
    });
    const parsed = regionListSchema.parse(res);
    return parsed.regions.map((r) => ({ id: r.id, name: r.name }));
  });

export const listCities = createServerFn()
  .inputValidator(z.object({ regionId: z.string().min(1) }))
  .handler(async ({ data }) => {
    const res = await medusa.client.fetch<unknown>("/store/ph-geo/cities", {
      query: { region_id: data.regionId },
    });
    const parsed = cityListSchema.parse(res);
    return parsed.cities.map((c) => ({ id: c.id, name: c.name }));
  });

export const listBarangays = createServerFn()
  .inputValidator(z.object({ cityId: z.string().min(1) }))
  .handler(async ({ data }) => {
    const res = await medusa.client.fetch<unknown>("/store/ph-geo/barangays", {
      query: { city_id: data.cityId },
    });
    const parsed = barangayListSchema.parse(res);
    return parsed.barangays.map((b) => ({ id: b.id, name: b.name }));
  });
